/** Yield: lock USD into BTC, ETH or USDT plans; flexible plans redeem any time, locked plans unlock early only through a request to our team. */
import { useEffect, useMemo, useState, type FormEvent } from 'react';
import {
  CONTACT_METHODS,
  D,
  fmtDate,
  fmtUsd,
  termLabel,
  UNLOCK_REASONS,
  YIELD_COIN_INFO,
  YIELD_COINS,
  type ContactMethod,
  type YieldCoin,
  type YieldOverview,
  type YieldPlan,
  type YieldStake,
} from '@platform/shared';
import { cx, Modal, Spinner } from '../../components/ui';
import { api } from '../../lib/api';
import { Link } from '../../lib/router';
import { useLoad } from '../../stores/account';
import { errMsg, Notice, PillButton } from '../account/kit';
import { Panel, type Msg } from './shared';

const DAY = 86_400_000;
const fmtQty = (x: string | number, coin: YieldCoin) => {
  const v = Number(x);
  return `${v.toLocaleString('en-US', { maximumFractionDigits: coin === 'USDT' ? 2 : 8 })} ${coin}`;
};
const interest = (qty: number, apy: number, days: number) => (qty * apy * days) / 36500;
const daysLeft = (s: YieldStake) => (s.unlockAt ? Math.max(0, Math.ceil((new Date(s.unlockAt).getTime() - Date.now()) / DAY)) : 0);

function CoinIcon({ coin, size = 44 }: { coin: YieldCoin; size?: number }) {
  const c = YIELD_COIN_INFO[coin];
  return (
    <span aria-hidden="true" className="grid shrink-0 place-items-center rounded-full font-bold text-white" style={{ width: size, height: size, background: c.color, fontSize: size * 0.42 }}>
      {c.icon}
    </span>
  );
}

function planTitle(p: Pick<YieldPlan, 'termDays'>) {
  return termLabel(p.termDays);
}

// ---------- staking card ----------

function StakeCard({ data, onStaked, onScrollToStakes }: { data: YieldOverview; onStaked: (m: Msg) => void; onScrollToStakes: () => void }) {
  const [coin, setCoin] = useState<YieldCoin>('BTC');
  const plans = useMemo(() => data.plans.filter((p) => p.coin === coin), [data.plans, coin]);
  const [planId, setPlanId] = useState<number | null>(null);
  const plan = plans.find((p) => p.id === planId) ?? plans[plans.length - 1] ?? null;
  const [amount, setAmount] = useState('1000');
  const [confirm, setConfirm] = useState(false);
  useEffect(() => setPlanId(null), [coin]);

  const price = data.prices[coin] ? Number(data.prices[coin]) : null;
  const usd = Number(amount) || 0;
  const qty = price ? usd / price : 0;
  const days = plan ? plan.termDays || 365 : 365;
  const est = plan ? interest(qty, Number(plan.apy), days) : 0;
  const stakedQty = data.stakes.filter((s) => s.status === 'active' && s.coin === coin).reduce((t, s) => t + Number(s.qty), 0);
  const best = plans.reduce((m, p) => Math.max(m, Number(p.apy)), 0);
  const avail = Number(data.available);
  const problem = !data.kycApproved
    ? null
    : !plan
      ? 'No plans are open for this coin right now.'
      : !price
        ? `${coin} price is unavailable right now.`
        : usd <= 0
          ? 'Enter an amount.'
          : usd < Number(plan.minStake)
            ? `The minimum is ${fmtUsd(plan.minStake)}.`
            : usd > avail
              ? `You have ${fmtUsd(data.available)} available.`
              : null;

  return (
    <div className="relative grid min-h-[560px] grid-cols-[minmax(0,1fr)] place-items-center overflow-hidden rounded-[22px] border border-line px-3 py-8 sm:px-4 sm:py-10" style={{ background: 'radial-gradient(70% 60% at 22% 18%, color-mix(in srgb, var(--accent) 22%, transparent), transparent 70%), radial-gradient(60% 60% at 82% 86%, color-mix(in srgb, var(--green) 16%, transparent), transparent 70%), var(--bg)' }}>
      <span aria-hidden="true" className="absolute top-[14%] left-[10%] size-28 rotate-[-14deg] rounded-[28px] border border-line-strong opacity-60 max-md:hidden" />
      <span aria-hidden="true" className="absolute bottom-[16%] left-[16%] size-16 rotate-[8deg] rounded-2xl border border-line-strong opacity-60 max-md:hidden" />
      <span aria-hidden="true" className="absolute right-[12%] bottom-[22%] size-20 rounded-full border border-up/40 opacity-70 max-md:hidden" />

      <div className="relative w-full max-w-[420px] min-w-0 rounded-[20px] border border-line-strong bg-panel shadow-[0_24px_80px_rgba(0,0,0,0.35)]">
        <div className="border-b border-line p-4">
          <div role="tablist" aria-label="Coin" className="grid grid-cols-3 gap-1 rounded-xl bg-track p-1">
            {YIELD_COINS.map((c) => (
              <button
                key={c}
                type="button"
                role="tab"
                aria-selected={coin === c}
                onClick={() => setCoin(c)}
                className={cx('flex h-9 items-center justify-center gap-1.5 rounded-lg text-[13px] font-semibold', coin === c ? 'bg-card text-fg shadow-sm' : 'text-muted hover:text-fg')}
              >
                <CoinIcon coin={c} size={18} />
                <span className="max-sm:hidden">{YIELD_COIN_INFO[c].name}</span>
                <span className="sm:hidden">{c}</span>
              </button>
            ))}
          </div>
        </div>

        <div className="flex flex-col gap-4 p-4">
          <div className="flex items-center gap-3">
            <CoinIcon coin={coin} />
            <div>
              <div className="text-[22px] leading-tight font-semibold">{coin}</div>
              <div className="text-[13.5px] font-semibold text-up">{best ? `Up to ${best}% APY` : 'No open plans'}</div>
            </div>
            <div className="flex-1" />
            {price && <div className="num text-right text-[12px] text-muted">1 {coin} = {fmtUsd(String(price))}</div>}
          </div>

          <div role="radiogroup" aria-label="Plan" className="flex flex-col gap-2">
            {plans.map((p) => {
              const on = plan?.id === p.id;
              return (
                <button
                  key={p.id}
                  type="button"
                  role="radio"
                  aria-checked={on}
                  onClick={() => setPlanId(p.id)}
                  className={cx('grid grid-cols-[auto_1fr_auto] items-center gap-3 rounded-xl border px-3 py-2.5 text-left', on ? 'border-accent bg-accent-bg' : 'border-line hover:border-line-strong')}
                >
                  <span className={cx('size-4 rounded-full', on ? 'border-[5px] border-accent' : 'border-2 border-line-strong')} />
                  <span className="min-w-0">
                    <span className="block text-[14px] font-semibold">{planTitle(p)}</span>
                    <span className="block truncate text-[12px] text-muted">{p.label || (p.termDays ? `Locked ${p.termDays} days` : 'Redeem any time')}</span>
                  </span>
                  <span className="num text-[15px] font-semibold text-up">{Number(p.apy)}%</span>
                </button>
              );
            })}
          </div>

          <div className="grid grid-cols-2 gap-2.5">
            <div className="min-w-0 rounded-xl border border-line px-3 py-2.5">
              <div className="text-[12px] text-muted">Staked balance</div>
              <div className="num truncate text-[15px] font-semibold">{fmtQty(stakedQty, coin)}</div>
              <div className="num text-[12px] text-muted">≈ {fmtUsd(String(stakedQty * (price ?? 0)))}</div>
            </div>
            <div className="min-w-0 rounded-xl border border-line px-3 py-2.5">
              <div className="text-[12px] text-muted">Available balance</div>
              <div className="num truncate text-[15px] font-semibold">{fmtUsd(data.available)}</div>
              <div className="text-[12px] text-muted">Live account</div>
            </div>
          </div>

          {data.kycApproved ? (
            <>
              <label className="flex items-center gap-2 rounded-xl border border-line-strong py-2 pr-2 pl-4 focus-within:border-accent">
                <input
                  id="yield-amount"
                  inputMode="decimal"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value.replace(/[^0-9.]/g, ''))}
                  aria-label="Amount in USDT"
                  className="num min-w-0 flex-1 bg-transparent text-[16px] font-semibold text-fg outline-none"
                />
                <span className="text-[13px] text-muted">USDT</span>
                <button type="button" onClick={() => setAmount(String(Math.floor(avail * 100) / 100))} className="rounded-md bg-accent-bg px-2 py-1 text-[12px] font-semibold text-accent-fg">
                  Max
                </button>
              </label>
              <div className="flex justify-between gap-3 text-[12.5px] text-muted">
                <span>{plan?.termDays ? `Interest over ${planTitle(plan)}` : 'Interest per year'}</span>
                <b className="num text-right text-fg">
                  {fmtUsd(String(est * (price ?? 0)))}
                  {coin !== 'USDT' && ` · ${fmtQty(est.toFixed(8), coin)}`}
                </b>
              </div>
            </>
          ) : (
            <div className="rounded-xl border border-warn/40 bg-warn-bg p-3 text-[13px]">
              <b>Verify your identity to use Yield.</b>{' '}
              <Link to="/account/kyc" className="font-semibold">
                Verify now
              </Link>
            </div>
          )}

          <div className="flex flex-col gap-1 rounded-xl border border-accent/35 bg-accent-bg p-3 text-[12.5px]">
            <b className="text-[13.5px]">Earn yield on {coin}</b>
            <span className="text-muted">
              {coin === 'USDT'
                ? 'Interest accrues daily in USDT.'
                : `Your USDT buys ${coin} at the current price. Interest accrues daily in ${coin}, and you get it back in USDT at the ${coin} price when the plan ends, so the value moves with ${coin}.`}{' '}
              {plan?.termDays ? `Locked plans unlock automatically after ${planTitle(plan)}.` : 'Flexible plans can be redeemed any time.'}
            </span>
          </div>
          {problem && data.kycApproved && <p className="m-0 text-[12.5px] text-down">{problem}</p>}
        </div>

        <div className="grid grid-cols-2 gap-2.5 border-t border-line p-4">
          <button type="button" onClick={onScrollToStakes} className="h-11 rounded-full bg-pill text-[14px] font-semibold text-fg">
            Unstake
          </button>
          <button type="button" disabled={!data.kycApproved || !!problem} onClick={() => setConfirm(true)} className="h-11 rounded-full bg-accent text-[14px] font-semibold text-white disabled:bg-track disabled:text-muted">
            Stake
          </button>
        </div>
      </div>
      {confirm && plan && price && <ConfirmModal plan={plan} usd={usd} price={price} onClose={() => setConfirm(false)} onDone={(m) => (setConfirm(false), onStaked(m))} />}
    </div>
  );
}

function ConfirmModal({ plan, usd, price, onClose, onDone }: { plan: YieldPlan; usd: number; price: number; onClose: () => void; onDone: (m: Msg) => void }) {
  const [ok, setOk] = useState(!plan.termDays);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const coin = plan.coin;
  const qty = usd / price;
  const unlock = plan.termDays ? new Date(Date.now() + plan.termDays * DAY) : null;
  const est = interest(qty, Number(plan.apy), plan.termDays || 365);
  async function go() {
    setBusy(true);
    setErr(null);
    try {
      const s = await api.post<YieldStake>('/api/yield/stake', { planId: plan.id, amount: usd.toFixed(2) });
      onDone({ ok: true, text: plan.termDays ? `${fmtQty(s.qty, coin)} locked until ${fmtDate(s.unlockAt!)}.` : `${fmtQty(s.qty, coin)} staked in the Flexible plan.` });
    } catch (e) {
      setErr(errMsg(e));
    } finally {
      setBusy(false);
    }
  }
  return (
    <Modal open onClose={onClose} title={plan.termDays ? 'Confirm lock' : 'Confirm stake'} width={420}>
      <div className="flex flex-col gap-4 text-[14px]">
        <div className="flex items-center gap-3">
          <CoinIcon coin={coin} />
          <div>
            <div className="num text-[20px] font-semibold">≈ {fmtQty(qty.toFixed(coin === 'USDT' ? 2 : 8), coin)}</div>
            <div className="num text-[13px] text-muted">
              {fmtUsd(usd.toFixed(2))} at {fmtUsd(String(price))} / {coin}
            </div>
          </div>
        </div>
        <dl className="m-0 flex flex-col gap-2 text-[13.5px]">
          {[
            ['Plan', `${planTitle(plan)} · ${Number(plan.apy)}% APY`],
            ['Locked from', fmtDate(new Date())],
            ['Unlocks on', unlock ? fmtDate(unlock) : 'Any time you redeem'],
            ['Interest paid', `Daily, in ${coin}`],
            [plan.termDays ? 'Est. interest' : 'Est. interest per year', `+${fmtQty(est.toFixed(coin === 'USDT' ? 2 : 8), coin)}`],
          ].map(([k, v]) => (
            <div key={k} className="flex justify-between gap-3">
              <dt className="text-muted">{k}</dt>
              <dd className={cx('num m-0 text-right font-medium', k!.startsWith('Est') && 'text-up')}>{v}</dd>
            </div>
          ))}
        </dl>
        {plan.termDays > 0 && (
          <>
            <div className="rounded-xl border border-warn/40 bg-warn-bg p-3 text-[13px]">
              <b>
                Your {coin} is locked until {fmtDate(unlock!)}.
              </b>{' '}
              To unlock early you send a request to our team, and you lose the interest earned so far. Locked funds can't be traded or withdrawn.
            </div>
            <label className="flex items-center gap-2.5 text-[13.5px]">
              <input id="lock-ok" type="checkbox" checked={ok} onChange={(e) => setOk(e.target.checked)} className="size-4 accent-[var(--accent)]" />I understand the lock period
            </label>
          </>
        )}
        {err && (
          <p role="alert" className="m-0 text-[13px] text-down">
            {err}
          </p>
        )}
        <div className="grid grid-cols-2 gap-2.5">
          <PillButton kind="secondary" size="sm" onClick={onClose}>
            Back
          </PillButton>
          <PillButton kind="accent" size="sm" busy={busy} disabled={!ok} onClick={() => void go()}>
            {plan.termDays ? 'Lock' : 'Stake'} {fmtUsd(usd.toFixed(2))}
          </PillButton>
        </div>
      </div>
    </Modal>
  );
}

// ---------- stakes ----------

function DaysRing({ s }: { s: YieldStake }) {
  const total = s.termDays;
  const left = daysLeft(s);
  const f = total ? Math.min(1, (total - left) / total) : 0;
  const r = 66;
  const c = 2 * Math.PI * r;
  return (
    <svg viewBox="0 0 160 160" className="mx-auto size-[140px]" role="img" aria-label={`${left} days left of ${total}`}>
      <circle cx={80} cy={80} r={r} fill="none" stroke="var(--border-strong)" strokeWidth={10} />
      <circle cx={80} cy={80} r={r} fill="none" stroke="var(--accent)" strokeWidth={10} strokeLinecap="round" strokeDasharray={c} strokeDashoffset={c * (1 - f)} transform="rotate(-90 80 80)" />
      <text x={80} y={78} textAnchor="middle" fill="var(--text)" fontSize={34} fontWeight={600} fontFamily="var(--font-mono)">
        {left}
      </text>
      <text x={80} y={100} textAnchor="middle" fill="var(--muted)" fontSize={12}>
        {left === 1 ? 'day left' : 'days left'}
      </text>
    </svg>
  );
}

function StakeTile({ s, price, onAction }: { s: YieldStake; price: number | null; onAction: (kind: 'redeem' | 'unlock', s: YieldStake) => void }) {
  const qty = Number(s.qty);
  const today = interest(qty, Number(s.apy), 1);
  const req = s.unlockRequest?.status === 'pending' ? s.unlockRequest : null;
  const rejected = s.unlockRequest?.status === 'rejected' ? s.unlockRequest : null;
  const total = s.termDays ? qty + Number(s.interestAtEnd) : null;
  return (
    <section className="flex flex-col gap-4 rounded-[18px] border border-line bg-panel p-4">
      <div className="flex items-center justify-between gap-2">
        <span className="flex items-center gap-2 font-semibold">
          <CoinIcon coin={s.coin} size={20} />
          {s.coin} · {termLabel(s.termDays)}
        </span>
        {req ? (
          <span className="rounded-md bg-warn-bg px-2 py-0.5 text-[12px] font-semibold text-warn">Unlock requested</span>
        ) : s.termDays ? (
          <span className="rounded-md bg-accent-bg px-2 py-0.5 text-[12px] font-semibold text-accent-fg">Locked</span>
        ) : (
          <span className="rounded-md bg-up-bg px-2 py-0.5 text-[12px] font-semibold text-up">Flexible</span>
        )}
      </div>
      {s.termDays > 0 && <DaysRing s={s} />}
      <div className="grid grid-cols-2 gap-2.5">
        <div className="min-w-0 rounded-xl border border-line px-3 py-2.5">
          <div className="text-[12px] text-muted">{s.termDays ? 'Locked amount' : 'Staked amount'}</div>
          <div className="num truncate text-[14.5px] font-semibold">{fmtQty(s.qty, s.coin)}</div>
          <div className="num text-[12px] text-muted">≈ {fmtUsd(String(qty * (price ?? Number(s.entryPrice))))}</div>
        </div>
        <div className="min-w-0 rounded-xl border border-line px-3 py-2.5">
          <div className="text-[12px] text-muted">Interest earned</div>
          <div className="num truncate text-[14.5px] font-semibold text-up">+{fmtQty(s.interest, s.coin)}</div>
          <div className="num truncate text-[12px] text-muted">+{Number(today.toFixed(s.coin === 'USDT' ? 2 : 8))} a day</div>
        </div>
      </div>
      {s.termDays > 0 && s.unlockAt && (
        <>
          <div className="grid grid-cols-[auto_1fr_auto] items-center gap-2 text-[11px] text-muted">
            <span className="num">{fmtDate(s.startedAt)}</span>
            <div className="h-1.5 overflow-hidden rounded-full bg-track">
              <div className="h-full rounded-full bg-accent" style={{ width: `${Math.min(100, ((s.termDays - daysLeft(s)) / s.termDays) * 100)}%` }} />
            </div>
            <span className="num">{fmtDate(s.unlockAt)}</span>
          </div>
          <div className="flex justify-between text-[12.5px] text-muted">
            <span>At unlock you receive</span>
            <b className="num text-fg">{fmtQty(total!.toFixed(s.coin === 'USDT' ? 2 : 8), s.coin)}</b>
          </div>
        </>
      )}
      {req && <p className="m-0 rounded-xl bg-warn-bg px-3 py-2 text-[12.5px]">Our team will contact you about your unlock request. Your plan stays locked until it's approved.</p>}
      {rejected && !req && <p className="m-0 rounded-xl bg-track px-3 py-2 text-[12.5px] text-muted">Your last unlock request was declined{rejected.note ? `: ${rejected.note}` : '.'}</p>}
      <div className="mt-auto grid grid-cols-1 gap-2">
        {s.termDays ? (
          <button type="button" disabled={!!req} onClick={() => onAction('unlock', s)} className="h-10 rounded-full border border-line-strong text-[13.5px] font-semibold text-fg disabled:opacity-50">
            {req ? 'Request sent' : 'Unlock early'}
          </button>
        ) : (
          <button type="button" onClick={() => onAction('redeem', s)} className="h-10 rounded-full bg-pill text-[13.5px] font-semibold text-fg">
            Redeem
          </button>
        )}
      </div>
    </section>
  );
}

function RedeemModal({ s, price, onClose, onDone }: { s: YieldStake; price: number | null; onClose: () => void; onDone: (m: Msg) => void }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const total = Number(s.qty) + Number(s.interest);
  return (
    <Modal open onClose={onClose} title="Redeem Flexible plan" width={400}>
      <div className="flex flex-col gap-4 text-[14px]">
        <p className="m-0 text-muted">
          You get back {fmtQty(total.toFixed(s.coin === 'USDT' ? 2 : 8), s.coin)} including interest, converted to USD at the current price{price ? ` (≈ ${fmtUsd(String(total * price))})` : ''}.
        </p>
        {err && (
          <p role="alert" className="m-0 text-[13px] text-down">
            {err}
          </p>
        )}
        <div className="grid grid-cols-2 gap-2.5">
          <PillButton kind="secondary" size="sm" onClick={onClose}>
            Cancel
          </PillButton>
          <PillButton
            kind="accent"
            size="sm"
            busy={busy}
            onClick={async () => {
              setBusy(true);
              try {
                const r = await api.post<YieldStake>(`/api/yield/stakes/${s.id}/redeem`);
                onDone({ ok: true, text: `${fmtUsd(r.usdOut!)} returned to your live account.` });
              } catch (e) {
                setErr(errMsg(e));
                setBusy(false);
              }
            }}
          >
            Redeem
          </PillButton>
        </div>
      </div>
    </Modal>
  );
}

/** Contact form sent to our team; nothing unlocks until they approve it. */
function UnlockRequestModal({ s, onClose, onDone }: { s: YieldStake; onClose: () => void; onDone: (m: Msg) => void }) {
  const [reason, setReason] = useState<string>(UNLOCK_REASONS[0]);
  const [method, setMethod] = useState<ContactMethod>('phone');
  const [phone, setPhone] = useState('');
  const [windowText, setWindowText] = useState('');
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const field = 'h-11 w-full rounded-xl border border-line bg-bg px-3.5 text-[14px] text-fg outline-none focus:border-line-strong';
  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    try {
      const r = await api.post<{ ticketNumber: number }>(`/api/yield/stakes/${s.id}/unlock-request`, { reason, contactMethod: method, phone, callbackWindow: windowText, message });
      onDone({ ok: true, text: `Unlock request sent (ticket T-${r.ticketNumber}). Our team will contact you.` });
    } catch (ex) {
      setErr(errMsg(ex));
      setBusy(false);
    }
  }
  return (
    <Modal open onClose={onClose} title="Request early unlock" width={460}>
      <form onSubmit={submit} className="flex flex-col gap-3.5 text-[14px]">
        <div className="rounded-xl border border-warn/40 bg-warn-bg p-3 text-[13px]">
          <b>
            {s.coin} · {termLabel(s.termDays)} unlocks on {fmtDate(s.unlockAt!)}.
          </b>{' '}
          Unlocking early needs approval from our team{Number(s.interest) > 0 ? `, and you lose the ${fmtQty(s.interest, s.coin)} interest earned so far` : ', and the plan earns no interest'}. We'll contact you to confirm.
        </div>
        <label className="flex flex-col gap-1.5">
          <span className="text-[13px] font-semibold">Reason</span>
          <select id="unlock-reason" value={reason} onChange={(e) => setReason(e.target.value)} className={field}>
            {UNLOCK_REASONS.map((r) => (
              <option key={r}>{r}</option>
            ))}
          </select>
        </label>
        <fieldset className="m-0 flex flex-col gap-1.5 border-0 p-0">
          <legend className="mb-1.5 text-[13px] font-semibold">How should we contact you?</legend>
          <div className="grid grid-cols-3 gap-1 rounded-xl bg-track p-1">
            {CONTACT_METHODS.map((c) => (
              <button
                key={c.id}
                type="button"
                aria-pressed={method === c.id}
                onClick={() => setMethod(c.id)}
                className={cx('h-9 rounded-lg text-[13px] font-semibold', method === c.id ? 'bg-card text-fg shadow-sm' : 'text-muted')}
              >
                {c.label}
              </button>
            ))}
          </div>
        </fieldset>
        {method !== 'email' && (
          <div className="grid gap-3 sm:grid-cols-2">
            <label className="flex flex-col gap-1.5">
              <span className="text-[13px] font-semibold">Phone number</span>
              <input id="unlock-phone" required value={phone} onChange={(e) => setPhone(e.target.value)} inputMode="tel" placeholder="+44 7700 900123" className={field} />
            </label>
            <label className="flex flex-col gap-1.5">
              <span className="text-[13px] font-semibold">Best time (optional)</span>
              <input id="unlock-window" value={windowText} onChange={(e) => setWindowText(e.target.value)} maxLength={60} placeholder="Weekdays 9:00–12:00" className={field} />
            </label>
          </div>
        )}
        <label className="flex flex-col gap-1.5">
          <span className="text-[13px] font-semibold">Message (optional)</span>
          <textarea id="unlock-message" value={message} onChange={(e) => setMessage(e.target.value)} maxLength={1000} rows={3} className={cx(field, 'h-auto py-2.5')} />
        </label>
        {err && (
          <p role="alert" className="m-0 text-[13px] text-down">
            {err}
          </p>
        )}
        <div className="grid grid-cols-2 gap-2.5">
          <PillButton kind="secondary" size="sm" onClick={onClose}>
            Cancel
          </PillButton>
          <PillButton type="submit" kind="accent" size="sm" busy={busy}>
            Send request
          </PillButton>
        </div>
      </form>
    </Modal>
  );
}

const STATUS_LABEL: Record<string, [string, string]> = {
  completed: ['Completed', 'bg-up-bg text-up'],
  redeemed: ['Redeemed', 'bg-up-bg text-up'],
  unlocked_early: ['Unlocked early', 'bg-warn-bg text-warn'],
};

export function YieldTab() {
  const { data, error, reload } = useLoad<YieldOverview>('/api/yield');
  const [msg, setMsg] = useState<Msg>(null);
  const [action, setAction] = useState<{ kind: 'redeem' | 'unlock'; s: YieldStake } | null>(null);
  const done = (m: Msg) => {
    setMsg(m);
    setAction(null);
    void reload();
  };
  if (!data) return <div className="grid min-h-[320px] place-items-center text-muted">{error ? <p>{error}</p> : <Spinner />}</div>;
  const active = data.stakes.filter((s) => s.status === 'active');
  const closed = data.stakes.filter((s) => s.status !== 'active');
  const price = (c: YieldCoin) => (data.prices[c] ? Number(data.prices[c]) : null);
  return (
    <>
      <Notice msg={msg} onClose={() => setMsg(null)} />
      <StakeCard data={data} onStaked={done} onScrollToStakes={() => document.getElementById('your-stakes')?.scrollIntoView({ behavior: 'smooth' })} />
      <div id="your-stakes" className="flex scroll-mt-6 flex-col gap-3">
        <h2 className="m-0 text-[17px] font-semibold">
          Your stakes <span className="num text-[14px] font-normal text-muted">{active.length} active</span>
        </h2>
        {active.length === 0 ? (
          <p className="m-0 rounded-[18px] border border-dashed border-line px-5 py-10 text-center text-muted">Nothing staked yet. Choose a coin and a plan above to start earning.</p>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
            {active.map((s) => (
              <StakeTile key={s.id} s={s} price={price(s.coin)} onAction={(kind, st) => setAction({ kind, s: st })} />
            ))}
          </div>
        )}
      </div>
      {closed.length > 0 && (
        <Panel title="History">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[620px] border-collapse text-left text-[13.5px]">
              <thead>
                <tr className="text-[12.5px] text-muted">
                  <th className="py-2 font-medium">Plan</th>
                  <th className="py-2 font-medium">Started</th>
                  <th className="py-2 font-medium">Ended</th>
                  <th className="py-2 font-medium">Status</th>
                  <th className="py-2 text-right font-medium">Interest</th>
                  <th className="py-2 text-right font-medium">Returned</th>
                </tr>
              </thead>
              <tbody>
                {closed.map((s) => {
                  const [l, cls] = STATUS_LABEL[s.status] ?? [s.status, 'bg-track text-muted'];
                  return (
                    <tr key={s.id} className="border-t border-line">
                      <td className="py-3">
                        <span className="flex items-center gap-2">
                          <CoinIcon coin={s.coin} size={18} />
                          {s.coin} · {termLabel(s.termDays)}
                        </span>
                      </td>
                      <td className="py-3 text-muted">{fmtDate(s.startedAt)}</td>
                      <td className="py-3 text-muted">{s.closedAt ? fmtDate(s.closedAt) : '—'}</td>
                      <td className="py-3">
                        <span className={cx('rounded-md px-2 py-0.5 text-[12px] font-semibold', cls)}>{l}</span>
                      </td>
                      <td className="num py-3 text-right">{D.from(s.interest).isZero() ? '—' : `+${fmtQty(s.interest, s.coin)}`}</td>
                      <td className="num py-3 text-right font-semibold">{s.usdOut ? fmtUsd(s.usdOut) : '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Panel>
      )}
      {action?.kind === 'redeem' && <RedeemModal s={action.s} price={price(action.s.coin)} onClose={() => setAction(null)} onDone={done} />}
      {action?.kind === 'unlock' && <UnlockRequestModal s={action.s} onClose={() => setAction(null)} onDone={done} />}
    </>
  );
}
